import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { FiSearch, FiX, FiArrowRight, FiCornerDownLeft } from 'react-icons/fi';

const SearchModal = ({ isOpen, onClose, onSelect, searchItems }) => {
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
      setQuery('');
    }
  }, [isOpen]);

  const filteredItems = query.trim() === '' 
    ? [] 
    : searchItems.filter(item => 
        item.title.toLowerCase().includes(query.toLowerCase()) || 
        item.content?.toLowerCase().includes(query.toLowerCase()) ||
        item.category?.toLowerCase().includes(query.toLowerCase())
      ).slice(0, 8);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] px-4">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm"
          />
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.95, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            className="relative w-full max-w-2xl bg-slate-900 border border-slate-800 rounded-xl shadow-2xl overflow-hidden"
            onKeyDown={handleKeyDown}
          >
            <div className="flex items-center px-4 py-3 border-b border-slate-800">
              <SafeIcon icon={FiSearch} className="text-slate-500 mr-3" size={20} />
              <input
                ref={inputRef}
                type="text"
                placeholder="Search for questions, scripts, or products..."
                className="flex-1 bg-transparent border-none outline-none text-white placeholder-slate-500 text-lg"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <button 
                onClick={onClose}
                className="p-1 hover:bg-slate-800 rounded-md text-slate-500 transition-colors"
              >
                <SafeIcon icon={FiX} size={20} />
              </button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto p-2">
              {query.trim() === '' ? (
                <div className="p-8 text-center">
                  <p className="text-slate-500 text-sm">Try searching for "free trial", "pricing", or "original stories"</p>
                </div>
              ) : filteredItems.length > 0 ? (
                <div className="space-y-1">
                  {filteredItems.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        onSelect(item.id);
                        onClose();
                      }}
                      className="w-full text-left p-3 rounded-lg hover:bg-blue-600/10 group flex items-center justify-between transition-colors border border-transparent hover:border-blue-500/30"
                    >
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-[10px] uppercase font-bold tracking-wider text-blue-400 bg-blue-400/10 px-1.5 py-0.5 rounded">
                            {item.category}
                          </span>
                          <h4 className="text-white font-medium text-sm group-hover:text-blue-400 transition-colors">
                            {item.title}
                          </h4>
                        </div>
                        {item.content && (
                          <p className="text-xs text-slate-500 line-clamp-1 group-hover:text-slate-400">
                            {item.content}
                          </p>
                        )}
                      </div>
                      <SafeIcon icon={FiArrowRight} className="text-slate-700 group-hover:text-blue-400 opacity-0 group-hover:opacity-100 transition-all -translate-x-2 group-hover:translate-x-0" />
                    </button>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <p className="text-slate-500 text-sm italic">No results found for "{query}"</p>
                </div>
              )}
            </div>

            <div className="px-4 py-2 bg-slate-800/50 border-t border-slate-800 flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <div className="flex items-center text-[10px] text-slate-500">
                  <kbd className="bg-slate-700 px-1.5 py-0.5 rounded mr-1 text-slate-300 font-sans">ESC</kbd> to close
                </div>
                <div className="flex items-center text-[10px] text-slate-500">
                  <kbd className="bg-slate-700 px-1.5 py-0.5 rounded mr-1 text-slate-300 font-sans tracking-tighter">Enter</kbd> to select
                </div>
              </div>
              <div className="text-[10px] font-medium text-slate-600 italic">
                Internal Sales OS v2.1
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default SearchModal;